import { useEffect, useRef } from 'react';

/**
 * How often to re-fetch the estimate while waiting for the deposit to settle on
 * Stripe return. The webhook is the source of truth; this poll (plus the fast-
 * path confirm below) just closes the 5–30s webhook-delivery gap.
 */
export const DEPOSIT_POLL_MS = 1500;

/**
 * A cache-key segment that changes when the customer lands back from a SUCCESSFUL
 * deposit checkout, so the estimate query refetches instead of serving the pre-
 * payment snapshot. `undefined` (no segment) on the normal / cancel paths.
 *
 * Shared by the public quote page and the client portal so both settle a deposit
 * return identically.
 */
export function depositSettlementToken(
  id: string,
  deposit: 'success' | 'cancel' | undefined,
): string | undefined {
  return deposit === 'success' ? `${id}:${deposit}` : undefined;
}

/**
 * The refetch interval for the estimate query: poll only while a success return
 * is still waiting for the deposit to register, otherwise stop. A bank payment
 * that registered as clearing stops it too — it settles in days, not seconds.
 * Returns `false` (react-query's "don't poll") in every other state.
 */
export function depositPollInterval(
  deposit: 'success' | 'cancel' | undefined,
  depositCollected: boolean | undefined,
  depositClearing?: boolean,
): number | false {
  return deposit === 'success' && !depositCollected && !depositClearing ? DEPOSIT_POLL_MS : false;
}

/**
 * Fast-path confirm on Stripe return: hit the synchronous confirm endpoint
 * instead of waiting for the webhook. Idempotent — the webhook still runs as the
 * safety net, and the confirm reconciles the same payment. Fires exactly once per
 * `sessionId`; on failure it releases the guard so the 1.5s poll (and the
 * webhook) still settle the deposit.
 *
 * Shared by both deposit surfaces; each passes its own surface-specific `confirm`
 * (public vs portal endpoint) and `onConfirmed` (cache invalidation).
 */
export function useDepositConfirmOnReturn(params: {
  deposit: 'success' | 'cancel' | undefined;
  sessionId: string | undefined;
  depositCollected: boolean | undefined;
  confirm: (sessionId: string) => Promise<unknown>;
  onConfirmed: () => void;
}): void {
  const { deposit, sessionId, depositCollected, confirm, onConfirmed } = params;
  const confirmedSessionRef = useRef<string | null>(null);

  useEffect(() => {
    if (deposit !== 'success') return;
    if (!sessionId) return;
    if (confirmedSessionRef.current === sessionId) return;
    if (depositCollected) return;
    confirmedSessionRef.current = sessionId;
    void confirm(sessionId)
      .then(() => onConfirmed())
      .catch(() => {
        // Fall back to the poll; the webhook will arrive shortly.
        confirmedSessionRef.current = null;
      });
    // `confirm`/`onConfirmed` are recreated per render; the ref-guard (not the
    // dep array) is what enforces once-per-session_id, matching the original.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deposit, sessionId, depositCollected]);
}
